import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { CheckoutStepService } from "@spartacus/checkout/base/components";
import { ActivatedRoute } from "@angular/router";
import { FormControl, FormGroup } from "@angular/forms";
import { catchError, finalize, first, map, switchMap, take, tap } from "rxjs/operators";
import { BehaviorSubject, Observable, Subject, Subscription } from "rxjs";
import { User, UserAccountFacade } from '@spartacus/user/account/root';
import { GenActiveCartService } from '../../cart/core/facade/active-cart.service';
import { WindowRef } from '@spartacus/core';
import { GenHandlingOption, GenOptionCode } from 'src/app/generac/models/cart.model';
import { GenCheckoutService } from 'src/app/generac/services/checkout.service';
import { Cart } from '@spartacus/cart/base/root';

@Component({
    selector: 'gen-checkout-special-handling',
    templateUrl: './checkout-special-handling.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class GenCheckoutSpecialHandlingComponent implements OnInit, OnDestroy {
  private formGroupSubscription: Subscription;
  private cartNumber: string;

  @Input() shippingGroupName: string;
  @Input() showFgAttentionMessage: boolean = false;

  formGroup = new FormGroup({});
  handlingOptions$ = new BehaviorSubject<GenHandlingOption[]>([]);
  optionsUpdated$ = new Subject();
  currentUser: User;
  internalCalculation: boolean;
  isResidentialDeliverySpecialOptionSelected: boolean;
  selectedAddress: any;
  isServicePartsEnabled: boolean;
  skipPaymentTypeStep: boolean = false;

  constructor(
      protected checkoutStepService: CheckoutStepService,
      protected activatedRoute: ActivatedRoute,
      protected winRef: WindowRef,
      private checkoutService: GenCheckoutService,
      private activeCartFacade: GenActiveCartService,
      private userAccount: UserAccountFacade,
  ) {
  }

  ngOnInit(): void {
    this.isServicePartsEnabled = JSON.parse(this.winRef.localStorage.getItem('isServicePartsEnabled'));
    this.userAccount.get().pipe(
      first(),
      switchMap(user => {
        this.currentUser = user;
        return this.initSpecialHandlingOptions();
      }))
    .subscribe();
  }

  ngOnDestroy(): void {
    this.formGroupSubscription.unsubscribe();
  }

  private initSpecialHandlingOptions(): Observable<GenHandlingOption[]> {
    return this.activeCartFacade.getActive().pipe(
        take(1),
        switchMap((cart: Cart) => {
          this.cartNumber = cart.code;
          this.selectedAddress = cart.deliveryAddress;
          this.skipPaymentTypeStep = cart?.wellsFargoCart;
          this.checkoutService.disableCheckoutSteps(cart.cartTypes, cart?.engineReplacement);
          this.internalCalculation = cart.internalCalculation;
          if(!this.isServicePartsEnabled) {
            this.shippingGroupName = cart?.shippingGroups?.[0]?.shippingGroupName;
          }
          return this.getSpecialHandlingOptions(cart.code, this.currentUser.uid, this.shippingGroupName);
        }),
        tap((options: GenHandlingOption[]) => {
          this.handlingOptions$.next(options);
          this.initFormGroup(options);
          const convertedOptions = this.convertOptionsFromHandlingOptions(options);
          this.updateSpecialHandlingOptions(convertedOptions);
          this.isResidentialDeliverySpecialOptionSelected = this.selectedAddress?.residential && options?.length > 0;
        })
      );
  }

  private getSpecialHandlingOptions(cartNumber: string, userId: string, shippingGroupName: string): Observable<GenHandlingOption[]> {
    return this.checkoutService.getSpecialHandlingOptions(cartNumber, userId, shippingGroupName);
  }

  private updateSpecialHandlingOptions(handlingOptions: string): void {
    this.checkoutService.updateSpecialHandlingOptions(this.cartNumber, this.currentUser.uid, this.shippingGroupName, handlingOptions)
        .pipe(
            take(1),
            tap(() => this.activeCartFacade.reloadActiveCart()),
            catchError(() => this.getSpecialHandlingOptions(this.cartNumber, this.currentUser.uid, this.shippingGroupName).pipe(
                tap((options: GenHandlingOption[]) => {
                  this.handlingOptions$.next(options);
                  this.initFormGroup(options);
                })
            )),
            finalize(() => this.optionsUpdated$.next(true))
        )
        .subscribe();
  }

  private initFormGroup(options: GenHandlingOption[]) {
      this.formGroup = new FormGroup({});
    options.forEach((option: GenHandlingOption) => {
      this.formGroup.addControl(option.code, new FormControl({value: option.selected, disabled: option.disabled}), { emitEvent: false})
    });

    if(this.formGroupSubscription) this.formGroupSubscription.unsubscribe();
    this.subscribeToFormValueChanges();
  }

  private subscribeToFormValueChanges(): void {
    this.formGroupSubscription =
        this.formGroup.valueChanges.pipe(
            map(() => this.formGroup.getRawValue()),
            switchMap((options: GenOptionCode) => {
                const convertedOptions = this.convertOptionsToString(options);
                this.updateSpecialHandlingOptions(convertedOptions);
                return this.optionsUpdated$;
            }),
            switchMap(() => this.getSpecialHandlingOptions(this.cartNumber, this.currentUser.uid, this.shippingGroupName)),
            tap((options: GenHandlingOption[]) => {
                this.handlingOptions$.next(options);
                const formGroupCodes = Object.keys(this.formGroup.getRawValue());
                const optionCodes = options.map(option => option.code);

                if (JSON.stringify(formGroupCodes) !== JSON.stringify(optionCodes)) {
                    this.initFormGroup(options);
                }
            })
        ).subscribe()
  }

  private convertOptionsToString(options: GenOptionCode): string {
    return Object.keys(Object.fromEntries(Object.entries(options).filter(([_, value]) => value))).join(',');
  }

  private convertOptionsFromHandlingOptions(options: GenHandlingOption[]): string {
      return options
          .filter(option => option.selected)
          .map(option => option.code)
          .join(',');
  }
}
